import type {
  UseInfiniteQueryResult,
  UseMutationResult,
  UseQueryResult,
} from '@tanstack/react-query';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { AddWipRecordRequestI, GetWipRecordsRequestI, UpdateWipRecordRequestI } from 'api/wip';
import {
  addWipRecordApi,
  getWipAgentsApi,
  getWipDealerSummaryApi,
  getWipOemFamilyClaimStatusesApi,
  getWipRecordsApi,
  updateWipRecordApi,
} from 'api/wip';
import { RequestQueryKeysE } from 'declarations/enums/react-query';
import type { ApiErrorI } from 'declarations/interfaces/errors';
import type {
  DealerCardResponseI,
  OemFamilyClaimStatusesI,
  WipAgentI,
  WipRecordListI,
} from 'declarations/interfaces/wip';

export const useFetchWipRecords = (
  request: GetWipRecordsRequestI,
  enabled = true
): UseQueryResult<WipRecordListI, ApiErrorI> =>
  useQuery<WipRecordListI, ApiErrorI>(
    [RequestQueryKeysE.FetchWipRecords, request],
    (): Promise<WipRecordListI> => getWipRecordsApi(request),
    {
      enabled,
      keepPreviousData: true,
      staleTime: 60000,
    }
  );

export const useFetchWipRecordsInfinite = (
  request: Omit<GetWipRecordsRequestI, 'paging'> & { pageSize: number },
  enabled = true
): UseInfiniteQueryResult<WipRecordListI, ApiErrorI> =>
  useInfiniteQuery<WipRecordListI, ApiErrorI>(
    [RequestQueryKeysE.FetchWipRecords, request],
    ({ pageParam = 1 }): Promise<WipRecordListI> =>
      getWipRecordsApi({
        ...request,
        paging: { currentPage: pageParam, countPerPage: request.pageSize },
      }),
    {
      enabled,
      getNextPageParam: (lastPage, pages): number | undefined => {
        const totalCount = lastPage?.totalCount ?? 0;
        const maxPages = Math.ceil(totalCount / request.pageSize);
        const nextPage = pages.length + 1;
        return nextPage <= maxPages ? nextPage : undefined;
      },
      keepPreviousData: true,
      staleTime: 60000,
    }
  );

export const useFetchWipDealerSummary = (): UseQueryResult<DealerCardResponseI, ApiErrorI> =>
  useQuery<DealerCardResponseI, ApiErrorI>(
    [RequestQueryKeysE.FetchWipDealerSummary],
    (): Promise<DealerCardResponseI> => getWipDealerSummaryApi(),
    { staleTime: 60000 }
  );

export const useFetchWipAgents = (): UseQueryResult<WipAgentI[], ApiErrorI> =>
  useQuery<WipAgentI[], ApiErrorI>(
    [RequestQueryKeysE.FetchWipAgents],
    (): Promise<WipAgentI[]> => getWipAgentsApi(),
    { staleTime: 300000 }
  );

export const useFetchWipOemFamilyClaimStatuses = (): UseQueryResult<
  OemFamilyClaimStatusesI[],
  ApiErrorI
> =>
  useQuery<OemFamilyClaimStatusesI[], ApiErrorI>(
    [RequestQueryKeysE.FetchWipOemFamilyClaimStatuses],
    (): Promise<OemFamilyClaimStatusesI[]> => getWipOemFamilyClaimStatusesApi(),
    { staleTime: 300000 }
  );

export const useUpdateWipRecord = (): UseMutationResult<
  void,
  ApiErrorI,
  UpdateWipRecordRequestI
> => {
  const queryClient = useQueryClient();

  return useMutation<void, ApiErrorI, UpdateWipRecordRequestI>({
    mutationKey: [RequestQueryKeysE.UpdateWipRecord],
    mutationFn: (request): Promise<void> => updateWipRecordApi(request),
    onSuccess: (): void => {
      queryClient.invalidateQueries([RequestQueryKeysE.FetchWipRecords]);
      queryClient.invalidateQueries([RequestQueryKeysE.FetchWipDealerSummary]);
    },
  });
};

export const useAddWipRecord = (): UseMutationResult<void, ApiErrorI, AddWipRecordRequestI> => {
  const queryClient = useQueryClient();

  return useMutation<void, ApiErrorI, AddWipRecordRequestI>({
    mutationKey: [RequestQueryKeysE.AddWipRecord],
    mutationFn: (request): Promise<void> => addWipRecordApi(request),
    onSuccess: (): void => {
      queryClient.invalidateQueries([RequestQueryKeysE.FetchWipRecords]);
      queryClient.invalidateQueries([RequestQueryKeysE.FetchWipDealerSummary]);
    },
  });
};
